import { useState } from "react";
import { useAuth } from "../../lib/auth";
import { apiJSON } from "../../lib/api";
import { useIntl } from "../../lib/i18n";
import { DataTable } from "../../components/DataTable";

type Match = {
  source: string;
  id: string;
  email?: string;
  name?: string;
  role?: string;
};

type Response = {
  query: string;
  matches: Match[];
};

export default function ConflictCheckPage() {
  const { session } = useAuth();
  const { t } = useIntl();
  const [query, setQuery] = useState("");
  const [data, setData] = useState<Response | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim().length < 2) {
      setError("query_too_short");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await apiJSON<Response>("/api/v1/tenant/conflict-check", {
        method: "POST",
        token: session?.token,
        body: JSON.stringify({ query }),
      });
      setData(resp);
    } catch (err) {
      setError(String((err as Error).message ?? err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-brand-900">{t("conflict.title")}</h1>
      <form className="mt-4 flex max-w-md gap-2" onSubmit={submit}>
        <input
          aria-label={t("conflict.form.query")}
          className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("conflict.form.query")}
        />
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500 disabled:opacity-50"
        >
          {loading ? "…" : t("conflict.form.submit")}
        </button>
      </form>
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      {data && (
        <DataTable
          rows={data.matches}
          empty={`No conflicts found for "${data.query}".`}
          columns={[
            { header: "Source", render: (r) => r.source.replace("_", " ") },
            { header: "Email", render: (r) => r.email ?? "—" },
            { header: "Name", render: (r) => r.name ?? "—" },
            { header: "Role", render: (r) => r.role ?? "—" },
          ]}
        />
      )}
    </div>
  );
}
